import {
  createCsvBlob
} from '../csvEncoding'

import {
  csvEscape,
  fileStamp,
  format2,
  formatMonthExportLabel,
  hexToRgb,
  downloadBlob,
  ensureStatisticsPdfFonts
} from './common'

export const buildTripsExportBlock = (
  stats,
  periodText,
  vehicleText,
  lang,
  t
) => {
  const legend = stats?.tripsLegend || []

  const vehicleColumns = legend.map((vehicle) => ({
    vehicleId: String(vehicle.vehicleId),
    label: `${vehicle.label || ''}${
      vehicle.licensePlate
        ? ` (${vehicle.licensePlate})`
        : ''
    }`
  }))

  const headers = [
    t('TRIPLOG.STATISTICS.MONTH', {
      defaultValue: 'Mesiac'
    }),
    ...vehicleColumns.map((vehicle) => vehicle.label),
    t('TRIPLOG.STATISTICS.SUMMARY.TOTAL_KM', {
      defaultValue: 'Najazdené km'
    })
  ]

  const rows = (stats?.tripsChartData || []).map((month) => {
    const valuesByVehicle = (month.segments || []).reduce(
      (acc, segment) => {
        acc[String(segment.vehicleId)] = Number(
          segment.value || 0
        )

        return acc
      },
      {}
    )

    return [
      month.monthLabel ||
        formatMonthExportLabel(month.month, lang),

      ...vehicleColumns.map((vehicle) =>
        format2(
          valuesByVehicle[vehicle.vehicleId] || 0
        )
      ),

      format2(month.total)
    ]
  })

  return {
    headers,
    rows,
    chartData: stats?.tripsChartData || [],
    legend,
    meta: {
      period: periodText,
      vehicle: vehicleText
    }
  }
}

export const exportTripsCSV = (block, t) => {
  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.TRIPS_TAB', {
        defaultValue: 'Jazdy'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const csv = [
    ...titleRows,
    block?.headers || [],
    ...(block?.rows || [])
  ]
    .map((row) =>
      row.map(csvEscape).join(';')
    )
    .join('\n')

  downloadBlob(
    createCsvBlob(csv),
    `trips_statistics_${fileStamp()}.csv`
  )
}

export const exportTripsXLS = async (block, t) => {
  const XLSX = await import('xlsx-js-style')

  const titleRows = [
    [
      t('TRIPLOG.STATISTICS.TITLE', {
        defaultValue: 'Štatistiky'
      })
    ],
    [
      t('TRIPLOG.STATISTICS.TRIPS_TAB', {
        defaultValue: 'Jazdy'
      })
    ],
    [],
    [
      t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
        defaultValue: 'Zobrazené obdobie'
      }),
      block?.meta?.period || ''
    ],
    [
      t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
        defaultValue: 'Zobrazené vozidlo'
      }),
      block?.meta?.vehicle || ''
    ],
    []
  ]

  const headers = block?.headers || []
  const rows = block?.rows || []

  const worksheetData = [
    ...titleRows,
    headers,
    ...rows
  ]

  const ws = XLSX.utils.aoa_to_sheet(worksheetData)

  const columnCount = Math.max(
    1,
    ...worksheetData.map((row) => row.length)
  )

  ws['!cols'] = Array.from(
    {
      length: columnCount
    },
    (_, columnIndex) => ({
      wch: columnIndex === 0 ? 24 : 20
    })
  )

  const mainTitleCell = XLSX.utils.encode_cell({
    r: 0,
    c: 0
  })

  if (ws[mainTitleCell]) {
    ws[mainTitleCell].s = {
      font: {
        name: 'Arial',
        sz: 14,
        bold: true
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  const subTitleCell = XLSX.utils.encode_cell({
    r: 1,
    c: 0
  })

  if (ws[subTitleCell]) {
    ws[subTitleCell].s = {
      font: {
        name: 'Arial',
        sz: 11,
        bold: true
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  }

  ;[3, 4].forEach((rowIndex) => {
    const labelCell = XLSX.utils.encode_cell({
      r: rowIndex,
      c: 0
    })

    if (ws[labelCell]) {
      ws[labelCell].s = {
        font: {
          name: 'Arial',
          sz: 10,
          bold: true
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    }

    const valueCell = XLSX.utils.encode_cell({
      r: rowIndex,
      c: 1
    })

    if (ws[valueCell]) {
      ws[valueCell].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    }
  })

  const headerRowIndex = titleRows.length

  headers.forEach((_, columnIndex) => {
    const cellAddress = XLSX.utils.encode_cell({
      r: headerRowIndex,
      c: columnIndex
    })

    if (!ws[cellAddress]) return

    ws[cellAddress].s = {
      font: {
        name: 'Arial',
        sz: 10,
        bold: true
      },
      fill: {
        fgColor: {
          rgb: 'F0F0F0'
        }
      },
      alignment: {
        horizontal: 'left',
        vertical: 'center'
      }
    }
  })

  rows.forEach((row, rowIndex) => {
    row.forEach((_, columnIndex) => {
      const cellAddress = XLSX.utils.encode_cell({
        r: headerRowIndex + 1 + rowIndex,
        c: columnIndex
      })

      if (!ws[cellAddress]) return

      ws[cellAddress].s = {
        font: {
          name: 'Arial',
          sz: 10
        },
        alignment: {
          horizontal: 'left',
          vertical: 'center'
        }
      }
    })
  })

  const wb = XLSX.utils.book_new()

  XLSX.utils.book_append_sheet(
    wb,
    ws,
    t('TRIPLOG.STATISTICS.TRIPS_TAB', {
      defaultValue: 'Jazdy'
    })
  )

  XLSX.writeFile(
    wb,
    `trips_statistics_${fileStamp()}.xlsx`
  )
}

export const exportTripsPDF = async (block, t) => {
  const { jsPDF } = await import('jspdf')
  const autoTableModule = await import('jspdf-autotable')

  const autoTable =
    autoTableModule.default ||
    autoTableModule.autoTable

  const doc = new jsPDF({
    orientation: 'landscape',
    unit: 'pt',
    format: 'a4'
  })

  await ensureStatisticsPdfFonts(doc)

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()

  const marginLeft = 55
  const marginRight = 25
  const chartTop = 98
  const chartHeight = 244

  const chartWidth =
    pageWidth - marginLeft - marginRight

  const chartData = block?.chartData || []
  const legend = block?.legend || []
  const headers = block?.headers || []
  const rows = block?.rows || []

  const maxValue = Math.max(
    1,
    ...chartData.map((month) =>
      Number(month.total || 0)
    )
  )

  doc.setFont('Roboto', 'bold')
  doc.setFontSize(14)

  doc.text(
    t('TRIPLOG.STATISTICS.TITLE', {
      defaultValue: 'Štatistiky'
    }),
    18,
    28
  )

  doc.setFontSize(11)

  doc.text(
    t('TRIPLOG.STATISTICS.TRIPS_TAB', {
      defaultValue: 'Jazdy'
    }),
    18,
    44
  )

  doc.setFontSize(9)

  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_PERIOD', {
      defaultValue: 'Zobrazené obdobie'
    })}: ${block?.meta?.period || ''}`,
    18,
    60
  )

  doc.text(
    `${t('TRIPLOG.STATISTICS.SHOWN_VEHICLE', {
      defaultValue: 'Zobrazené vozidlo'
    })}: ${block?.meta?.vehicle || ''}`,
    18,
    74
  )

  const tickCount = 5

  doc.setFont('Roboto', 'normal')
  doc.setFontSize(8)

  for (let i = 0; i <= tickCount; i += 1) {
    const value =
      (maxValue / tickCount) * i

    const y =
      chartTop +
      chartHeight -
      (value / maxValue) * chartHeight

    doc.setDrawColor(232, 232, 232)
    doc.setLineWidth(0.5)

    doc.line(
      marginLeft,
      y,
      marginLeft + chartWidth,
      y
    )

    doc.setTextColor(80, 80, 80)

    doc.text(
      format2(value),
      marginLeft - 8,
      y + 3,
      {
        align: 'right'
      }
    )
  }

  const groupWidth =
    chartData.length > 0
      ? chartWidth / chartData.length
      : chartWidth

  const barWidth = Math.min(
    54,
    groupWidth * 0.58
  )

  chartData.forEach((month, monthIndex) => {
    const barX =
      marginLeft +
      monthIndex * groupWidth +
      (groupWidth - barWidth) / 2

    let currentY =
      chartTop + chartHeight

;(month.segments || []).forEach((segment) => {
  const value = Number(segment.value || 0)

  if (value <= 0) return

  const segmentHeight =
    (value / maxValue) * chartHeight

  const segmentTop = currentY - segmentHeight

  const [r, g, b] = hexToRgb(segment.color)

  doc.setFillColor(r, g, b)

doc.roundedRect(
  barX,
  segmentTop,
  barWidth,
  segmentHeight,
  4,
  4,
  'F'
)

  const vehicle = legend.find(
    (item) =>
      String(item.vehicleId) ===
      String(segment.vehicleId)
  )

  const plate = vehicle?.licensePlate
    ? ` (${vehicle.licensePlate})`
    : ''

  const label =
    `${segment.label || vehicle?.label || ''}${plate} • ` +
    `${format2(value)} km`

  doc.setFont('Roboto', 'normal')
  doc.setFontSize(6)
  doc.setTextColor(255, 255, 255)

  const labelLines = doc.splitTextToSize(
    label,
    Math.max(barWidth - 6, 10)
  )

  const lineHeight = 7
  const textHeight = labelLines.length * lineHeight

  if (
    segmentHeight >= textHeight + 4 &&
    barWidth >= 24
  ) {
    doc.text(
      labelLines,
      barX + barWidth / 2,
      segmentTop +
        segmentHeight / 2 -
        textHeight / 2 +
        lineHeight,
      {
        align: 'center'
      }
    )
  }

  currentY -= segmentHeight
})

    doc.setFont('Roboto', 'normal')
    doc.setFontSize(8)
    doc.setTextColor(0, 0, 0)

    doc.text(
      String(
        month.monthLabel ||
          month.month ||
          ''
      ),
      barX + barWidth / 2,
      chartTop + chartHeight + 15,
      {
        align: 'center'
      }
    )
  })

  let legendX = 18
  let legendY = chartTop + chartHeight + 42

  legend.forEach((item) => {
    const label = `${item.label || ''}${
      item.licensePlate
        ? ` (${item.licensePlate})`
        : ''
    }`

    const textWidth =
      doc.getTextWidth(label) + 30

    if (legendX + textWidth > pageWidth - 18) {
      legendX = 18
      legendY += 18
    }

    const [r, g, b] = hexToRgb(item.color)

    doc.setFillColor(r, g, b)

    doc.rect(
      legendX,
      legendY - 8,
      10,
      10,
      'F'
    )

    doc.setTextColor(0, 0, 0)
    doc.setFontSize(8)

    doc.text(
      label,
      legendX + 15,
      legendY
    )

    legendX += textWidth
  })

  let tableStartY = legendY + 20

  if (tableStartY > pageHeight - 100) {
    doc.addPage()

    tableStartY = 28
  }

  const tableMarginLeft = 18
  const tableMarginRight = 18

  const tableWidth =
    pageWidth -
    tableMarginLeft -
    tableMarginRight

  const columnWidth =
    tableWidth /
    Math.max(headers.length, 1)

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: tableStartY,
    theme: 'grid',

    margin: {
      top: 18,
      right: tableMarginRight,
      bottom: 18,
      left: tableMarginLeft
    },

    tableWidth,

    styles: {
      font: 'Roboto',
      fontStyle: 'normal',
      fontSize: 8,
      cellPadding: 3,
      overflow: 'linebreak',
      valign: 'middle',
      halign: 'left',
      lineWidth: 0.3,
      lineColor: [0, 0, 0],
      minCellHeight: 12
    },

    headStyles: {
      font: 'Roboto',
      fontStyle: 'bold',
      fontSize: 8,
      fillColor: [240, 240, 240],
      textColor: [0, 0, 0],
      lineWidth: 0.4,
      lineColor: [0, 0, 0]
    },

    bodyStyles: {
      font: 'Roboto',
      fontStyle: 'normal',
      textColor: [0, 0, 0],
      lineWidth: 0.3,
      lineColor: [0, 0, 0]
    },

    columnStyles: headers.reduce(
      (acc, _header, index) => {
        acc[index] = {
          cellWidth: columnWidth
        }

        return acc
      },
      {}
    ),

    horizontalPageBreak: false,
    pageBreak: 'auto',
    rowPageBreak: 'avoid'
  })

  downloadBlob(
    doc.output('blob'),
    `trips_statistics_${fileStamp()}.pdf`
  )
}